import { useEffect, useState } from "react";
import { PublicLayout } from "@/components/public/PublicLayout";
import { SEO } from "@/components/SEO";
import { Reveal } from "@/hooks/useReveal";
import { publicApi } from "@/lib/api";
import { useBranding } from "@/contexts/BrandingContext";
import { User, Star } from "lucide-react";

/** Compact member card used in the Yönetim Kurulu grid. */
const MemberCard = ({ person }) => (
    <div className="bg-liv-card border border-liv-border hover:border-liv-yellow/60 transition-colors group" data-testid={`management-card-${person.id}`}>
        <div className="aspect-[4/5] relative overflow-hidden bg-black">
            {person.photo_url ? (
                <img src={person.photo_url} alt={person.name} className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500" />
            ) : (
                <div className="absolute inset-0 flex items-center justify-center text-neutral-700">
                    <User className="w-16 h-16" />
                </div>
            )}
            {person.is_divan && (
                <div className="absolute top-2 left-2 inline-flex items-center gap-1 bg-liv-yellow text-black px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest shadow-md" data-testid={`management-divan-badge-${person.id}`}>
                    <Star className="w-3 h-3" />
                    Divan Kurulu
                </div>
            )}
        </div>
        <div className="p-4">
            <div className="text-[10px] uppercase tracking-widest text-liv-yellow leading-tight">{person.role}</div>
            <div className="font-display text-lg md:text-xl uppercase mt-1 leading-tight">{person.name}</div>
        </div>
    </div>
);

/** Large hero-style card for the Başkan. */
const PresidentCard = ({ person }) => (
    <div className="flex flex-col md:flex-row bg-liv-card border border-liv-border overflow-hidden" data-testid={`management-president-${person.id}`}>
        <div className="relative md:w-2/5 aspect-[4/5] md:aspect-auto md:min-h-[500px] bg-black overflow-hidden flex-shrink-0">
            {person.photo_url ? (
                <img src={person.photo_url} alt={person.name} className="absolute inset-0 w-full h-full object-cover" />
            ) : (
                <div className="absolute inset-0 flex items-center justify-center text-neutral-700">
                    <User className="w-32 h-32" />
                </div>
            )}
            {person.is_divan && (
                <div className="absolute top-4 left-4 inline-flex items-center gap-1 bg-liv-yellow text-black px-3 py-1 text-xs font-bold uppercase tracking-widest shadow-md">
                    <Star className="w-3.5 h-3.5" />
                    Divan Kurulu
                </div>
            )}
        </div>
        <div className="md:w-3/5 p-6 md:p-12 flex flex-col justify-center">
            <div className="text-[11px] uppercase tracking-[0.3em] text-liv-yellow">{person.role || "Başkan"}</div>
            <h2 className="font-display text-4xl md:text-6xl uppercase mt-2 leading-none">{person.name}</h2>
            {person.bio && <p className="text-neutral-300 mt-5 leading-relaxed max-w-xl">{person.bio}</p>}
        </div>
    </div>
);

/** Sort: by `order` ascending; within same order, Divan members come first. */
const sortMembers = (arr) => [...(arr || [])].sort((a, b) => {
    const ao = a.order ?? 999;
    const bo = b.order ?? 999;
    if (ao !== bo) return ao - bo;
    // Same order — divan first
    return (b.is_divan ? 1 : 0) - (a.is_divan ? 1 : 0);
});

const Management = () => {
    const { branding } = useBranding();
    const [data, setData] = useState({ baskan: [], divan: [], yonetim: [] });
    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        publicApi.management()
            .then((d) => { setData(d || { baskan: [], divan: [], yonetim: [] }); setLoaded(true); })
            .catch(() => setLoaded(true));
    }, []);

    const clubName = branding?.short_name || branding?.name || "Kulüp";

    // Backward-compat: if any legacy `divan`-category members exist and are NOT already
    // in the yönetim list, merge them in. New schema uses `is_divan` flag on yönetim members.
    const yonetimMembers = sortMembers([
        ...(data.yonetim || []),
        ...(data.divan || []).filter((d) => !(data.yonetim || []).some((y) => y.id === d.id)),
    ]);
    const baskan = (data.baskan || [])[0];
    const empty = loaded && !baskan && yonetimMembers.length === 0;

    return (
        <PublicLayout>
            <SEO title={`Yönetim · ${clubName}`} description={`${clubName} başkan ve yönetim kurulu üyeleri.`} />

            <section className="relative h-[40vh] md:h-[55vh] bg-black overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-black via-neutral-900 to-black opacity-90" />
                <div className="absolute inset-0 hero-overlay" />
                <div className="relative container-x h-full flex flex-col justify-end pb-10 md:pb-14">
                    <div className="overline mb-2">Kulüp</div>
                    <h1 className="font-display text-6xl md:text-9xl uppercase" data-testid="management-page-title">Yönetim</h1>
                    <p className="mt-3 max-w-2xl text-neutral-300">Başkanımız ve Yönetim Kurulu üyelerimiz.</p>
                </div>
            </section>

            {empty && (
                <div className="container-x py-20 text-center text-neutral-400" data-testid="management-empty-state">
                    Yönetim üyeleri henüz eklenmedi.
                </div>
            )}

            {baskan && (
                <section className="container-x py-12 md:py-16">
                    <Reveal>
                        <div className="mb-8 md:mb-10">
                            <div className="overline">Onursal Görev</div>
                            <h2 className="font-display text-4xl md:text-6xl uppercase mt-2">Başkan</h2>
                        </div>
                    </Reveal>
                    <PresidentCard person={baskan} />
                </section>
            )}

            {yonetimMembers.length > 0 && (
                <section className="container-x py-12 md:py-16" data-testid="management-section-yonetim">
                    <Reveal>
                        <div className="mb-8 md:mb-10">
                            <div className="overline">Operasyonel Liderlik</div>
                            <h2 className="font-display text-4xl md:text-6xl uppercase mt-2">Yönetim Kurulu</h2>
                            <p className="text-sm text-neutral-400 mt-2">
                                <Star className="w-3.5 h-3.5 inline-block text-liv-yellow mr-1" />
                                ile işaretli üyeler aynı zamanda <span className="text-liv-yellow font-semibold">Divan Kurulu</span> üyesidir.
                            </p>
                        </div>
                    </Reveal>
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
                        {yonetimMembers.map((m) => <MemberCard key={m.id} person={m} />)}
                    </div>
                </section>
            )}
        </PublicLayout>
    );
};

export default Management;
